import { Component, EventEmitter, OnDestroy, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AssessmentService } from 'src/app/services/assessment.service';
import { COUNTRY_LIST, SECTOR_LIST } from '../../helpers/data';
import { debounceTime, distinctUntilChanged, of, Subject, Subscription, switchMap, tap } from 'rxjs';
import { Questionnaire } from '../../models/questionnaire.model';
import { ExamineeService } from 'src/app/services/examinee.service';
import { AuthService } from 'src/app/services/auth.service';
import { Assessment, Associate } from '../../models/assessment.model';

@Component({
    selector: 'app-create-assessment',
    templateUrl: './create-assessment.component.html',
    styleUrl: './create-assessment.component.scss',
    standalone: false
})
export class CreateAssessmentComponent implements OnDestroy {
  @Output() conversationChanged: EventEmitter<any> = new EventEmitter();
  @ViewChild('createAssessmentModal') createAssessmentModal!: TemplateRef<any>;

  @Output() assessmentChanged: EventEmitter<any> = new EventEmitter();

  // --- Subscriptions for proper cleanup ---
  private searchSubscription?: Subscription;
  private usersSearchSubscription?: Subscription;
  private questionnaireSearchSubscription?: Subscription;
  private associationChoiceSubscription?: Subscription;

  // --- Form and State Properties ---
  mainForm!: FormGroup;
  assessment: Assessment | null = null;
  caseOwnerName: string | null = null;

  submitted = false;
  spinner = false;
  success = false;
  message = '';
  isSubjectFinalized = false;
  isAssociationFinalized = false;
  selectedSubForm: 'conversation' | 'examinee' | null = null;
  
  // For subject search
  searchResults: any[] = [];
  isSearching = false;
  showResultsTable = false;
  exactMatchFound = false;
  subjectSelected = false;

  // For associate section
  isAddingNewAssociate = false;
  availableAssociates: Associate[] = [];
  newAssociateAdded = false;

  // For owner search
  private usersSearchSubject = new Subject<string | null>();
  users: any[] = [];
  usersLoading = false;

  // For questionnaire section
  private questionnaireSearchSubject = new Subject<string | null>();
  questionnaires: Questionnaire[] = [];
  questionnaireLoading = true;
  
  // --- Data & Config ---
  projectID!: number;
  minExpiryDate: Date = new Date();
  createAssessmentModalRef: any;
  countriesList = COUNTRY_LIST;
  sectorList = SECTOR_LIST;

  isReInvite = false;
  reInviteExamineeId: number | null = null;

  constructor(public assessmentService: AssessmentService, private examineeService: ExamineeService, private authService: AuthService, private router: Router, private modalService: NgbModal, private fb: FormBuilder) { }

  openModal(projectID: number, caseContext?: Assessment, isReInvite: boolean = false, activityData?: any) {
    this.resetAllState();    
    this.projectID = projectID;
    this.isReInvite = isReInvite;

    this.mainForm = this.fb.group({
      subjectInfo: this.fb.group({
        subject_id: ['', Validators.required],
        case_owner_id: [{ value: null, disabled: true }, Validators.required],
        first_name: [{ value: '', disabled: true }, Validators.required],
        last_name: [{ value: '', disabled: true }, Validators.required]
      }),
      associationDetails: this.fb.group({
        hasAssociation: [null, Validators.required],
        selectedAssociateId: [null],
        newAssociateDetails: this.fb.group({
          associate_id: ['', Validators.required],
          first_name: ['', Validators.required],
          last_name: ['', Validators.required],
          role: ['', Validators.required]
        })
      }),
      conversationDetails: this.fb.group({
        name: ['', [Validators.required]],
        email: ['', [Validators.required, Validators.email]],
        country: [null],
        sector: [null]
      }),
      examineeDetails: this.fb.group({
        questionnaireId: [null, Validators.required],
        email: ['', [Validators.required, Validators.email]],
        time_expiration: [null, [Validators.required]]
      })
    });
    
    this.disableAllSections();

    if (caseContext) {
      this.patchAndLockExistingAssessment(caseContext);
    } else {
      this.setupSubjectSearch(projectID);
      this.setupUsersSearch();
    }

    this.setupAssociationChoiceListener();    
    this.setupQuestionnaireSearch();

    if (this.isReInvite && activityData) {
      this.reInviteExamineeId = activityData.id;
      this.questionnaireLoading = false;

      this.isAssociationFinalized = true; 
      this.selectedSubForm = 'examinee';

      if (activityData.questionnaire) {
        this.questionnaires = [activityData.questionnaire];
        this.mainForm.get('examineeDetails')?.patchValue({
          questionnaireId: activityData.questionnaire.id
        });
      } else {
        this.mainForm.get('examineeDetails')?.patchValue({
          questionnaireId: activityData.questionnaire_id
        });
      }

      if (activityData.associate) {
        this.availableAssociates = [activityData.associate];
        
        this.associationDetails.patchValue({
          hasAssociation: 'yes',
          selectedAssociateId: activityData.associate.associate_id
        });
      } else {
        this.associationDetails.patchValue({ hasAssociation: 'no' });
      }

      this.mainForm.get('examineeDetails')?.patchValue({
        email: activityData.email
      });

      this.mainForm.get('examineeDetails')?.enable();
      this.mainForm.get('examineeDetails')?.get('questionnaireId')?.disable();
      this.mainForm.get('examineeDetails')?.get('email')?.disable();
      this.mainForm.get('examineeDetails')?.get('time_expiration')?.enable();
      this.associationDetails.disable();
    }

    this.minExpiryDate.setDate(this.minExpiryDate.getDate());
    this.createAssessmentModalRef = this.modalService.open(this.createAssessmentModal, { size: 'xl', backdrop: 'static', keyboard: false });
  }

  get hasExistingAssociate(): boolean {
    return this.associationDetails.get('hasAssociation')?.value === 'yes';
  }

  // --- Getters ---
  get subjectInfo(): FormGroup { return this.mainForm.get('subjectInfo') as FormGroup; }
  get associationDetails(): FormGroup { return this.mainForm.get('associationDetails') as FormGroup; }
  get subjectInfoControls() { return this.subjectInfo.controls; }
  get conversationControls() { return (this.mainForm.get('conversationDetails') as FormGroup).controls; }
  get examineeControls() { return (this.mainForm.get('examineeDetails') as FormGroup).controls; }
  get newAssociateControls() { return (this.associationDetails.get('newAssociateDetails') as FormGroup).controls; }

  // --- Setup & Data Loading ---
  private resetAllState(): void {
    this.assessment = null;
    this.newAssociateAdded = false;
    this.caseOwnerName = null;
    this.submitted = false;
    this.spinner = false;
    this.isSubjectFinalized = false;
    this.showResultsTable = false;
    this.isAssociationFinalized = false;
    this.isAddingNewAssociate = false;
    this.subjectSelected = false;
    this.selectedSubForm = null;
    // this.associationDetails?.get('newAssociateDetails')?.disable();
    // this.associationDetails?.get('newAssociateDetails')?.reset();
    this.unsubscribeAll();
  }

  private disableAllSections(): void {
    this.associationDetails.disable();
    this.mainForm.get('conversationDetails')?.disable();
    this.mainForm.get('examineeDetails')?.disable();
  }

  private patchAndLockExistingAssessment(assessment: Assessment): void {
    this.assessment = assessment;
    this.subjectInfo.patchValue({
      subject_id: assessment.subject_id,
      case_owner_id: assessment.case_owner_id,
      first_name: assessment.subject_details.first_name,
      last_name: assessment.subject_details.last_name
    });

    if (assessment.case_owner_user?.person) {
      this.caseOwnerName = `${assessment.case_owner_user.person.first_name} ${assessment.case_owner_user.person.last_name}`;
    } else {
      this.caseOwnerName = `User ID: ${assessment.case_owner_id}`;
    }

    this.subjectInfo.disable();
    this.subjectSelected = true;
    this.isSubjectFinalized = true;
    this.associationDetails.enable();
    this.associationDetails.get('newAssociateDetails')?.disable();
  }

  private setupUsersSearch(): void {
    this.usersSearchSubscription = this.usersSearchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(searchText => {
      this.getUserList(searchText || '');
    });
  }

  getUserList(searchText = ''): void {
    this.usersLoading = true;
    this.assessmentService.getProjectMembers(this.projectID, { searchText }).subscribe({
      next: (response) => {
        this.users = response;
        this.usersLoading = false;
      },
      error: (err) => {
        this.users = [];
        this.authService.toastErrorMessage(err.message, 'Failed to fetch users list!');
        this.usersLoading = false;
      }
    });
  }

  onSearchUser(event: { term: string }): void {
    this.usersSearchSubject.next(event.term);
  }

  customSearchFn(term: string, item: any) {
    return true;
  }

  private setupSubjectSearch(projectID: number): void {
    this.searchSubscription = this.subjectInfo.get('subject_id')?.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(value => {
        this.isSubjectFinalized = false;
        this.selectedSubForm = null;
        this.subjectInfo.get('case_owner_id')?.disable();
        this.subjectInfo.get('first_name')?.disable();
        this.subjectInfo.get('last_name')?.disable();
        this.isSearching = true;
        this.showResultsTable = !!value;
        this.exactMatchFound = false;
      }),
      switchMap(id => {
        if (!id) return of([]);
        const params = {
          searchText: id
        };
        return this.assessmentService.searchAssessmentsBySubjectId(projectID, params);
      })
    ).subscribe(subjects => {
      const searchTerm = this.subjectInfo.get('subject_id')?.value.toLowerCase();
      this.exactMatchFound = subjects.some(subject => subject.subject_id.toLowerCase() === searchTerm);
      
      this.searchResults = subjects;
      this.isSearching = false;
    });
  }

  private setupAssociationChoiceListener(): void {
    const hasAssociationControl = this.associationDetails.get('hasAssociation');
    const selectedAssociateIdControl = this.associationDetails.get('selectedAssociateId');
    const newAssociateDetailsControl = this.associationDetails.get('newAssociateDetails');

    this.associationChoiceSubscription = hasAssociationControl?.valueChanges.subscribe(value => {
      this.isAddingNewAssociate = false;
      this.newAssociateAdded = false;
      newAssociateDetailsControl?.disable();
      newAssociateDetailsControl?.reset();
      
      if (value === 'no') {
        this.isAssociationFinalized = true;
        selectedAssociateIdControl?.reset();
        selectedAssociateIdControl?.clearValidators();
      } else if (value === 'yes') {
        this.isAssociationFinalized = false; 
        newAssociateDetailsControl?.setValidators(Validators.required);
        this.availableAssociates = this.assessment?.associates || [];
      }
      selectedAssociateIdControl?.updateValueAndValidity();
    });
  }

  private setupQuestionnaireSearch(): void {
    this.questionnaireSearchSubscription = this.questionnaireSearchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(searchText => {
      this.getQuestionnaireList(searchText || '');
    });
  }

  getQuestionnaireList(searchText = ''): void {
    this.questionnaireLoading = true;
    this.examineeService.getProjectQuestionnairesList(this.projectID, { searchText }).subscribe({
      next: (response) => {
        this.questionnaires = response;
        this.questionnaireLoading = false;
      },
      error: (err) => {
        this.questionnaires = [];
        this.authService.toastErrorMessage(err.message, 'Failed to fetch questionnaire list!');
        this.questionnaireLoading = false;
      }
    });
  }

  onSearchQuestionnaire(event: { term: string }): void {
    this.questionnaireSearchSubject.next(event.term);
  }
  
  // --- Event Handlers ---
  onSubjectSelect(subject: any): void {
    this.assessment = subject;
    this.searchSubscription?.unsubscribe();

    this.subjectInfo.patchValue({
      subject_id: subject.subject_id,
      case_owner_id: subject.case_owner_id,
      first_name: subject.subject_details.first_name,
      last_name: subject.subject_details.last_name
    }, { emitEvent: false });

    if (subject.case_owner_user?.person) {
      this.caseOwnerName = `${subject.case_owner_user.person.first_name} ${subject.case_owner_user.person.last_name}`;
    } else {
      this.caseOwnerName = 'Unknown';
    }

    this.subjectInfo.clearValidators();
    this.subjectInfo.updateValueAndValidity();
    this.subjectInfo.disable();

    this.subjectSelected = true;
    this.isSubjectFinalized = true;
    this.showResultsTable = false;
    this.associationDetails.enable();
    this.associationDetails.get('newAssociateDetails')?.disable();
  }

  onCreateNew(): void {
    this.searchSubscription?.unsubscribe();
    this.subjectInfo.get('subject_id')?.disable();
    this.subjectInfo.get('case_owner_id')?.enable();
    this.subjectInfo.get('first_name')?.enable();
    this.subjectInfo.get('last_name')?.enable();

    this.getUserList();

    this.isSubjectFinalized = true;
    this.showResultsTable = false;
    this.associationDetails.enable();
    this.associationDetails.get('newAssociateDetails')?.disable();
  }

  onAssociateSelect(associate: Associate): void {
    this.associationDetails.get('selectedAssociateId')?.setValue(associate.associate_id);
    this.isAssociationFinalized = true;
  }

  showAddNewAssociateForm(): void {
    this.isAddingNewAssociate = true;
    this.associationDetails.get('newAssociateDetails')?.enable();
  }

  cancelAddNewAssociate(): void {
    this.isAddingNewAssociate = false;
    this.isAddingNewAssociate = false;
    this.associationDetails.get('newAssociateDetails')?.disable();
    this.associationDetails.get('newAssociateDetails')?.reset();
  }

  onSaveNewAssociate(): void {
    const newAssociateForm = this.associationDetails.get('newAssociateDetails');
    if (newAssociateForm?.invalid) {
      newAssociateForm.markAllAsTouched();
      return;
    }
    
    const formValue = newAssociateForm?.value;

    const idExists = this.availableAssociates.some(
      assoc => assoc.associate_id.toLowerCase() === formValue.associate_id.toLowerCase()
    );

    if (idExists) {
      console.error("An associate with this ID already exists in the list.");
      this.authService.toastErrorMessage('An associate with this ID already exists.', 'Duplicate ID');
      return;
    }

    const newAssociate: Associate = {
      associate_id: formValue.associate_id,
      status: 'ACTIVE',
      associate_details: {
        first_name: formValue.first_name,
        last_name: formValue.last_name,
        role: formValue.role
      }
    };

    this.availableAssociates.push(newAssociate);
    this.onAssociateSelect(newAssociate);

    this.isAssociationFinalized = true;
    this.newAssociateAdded = true;
    this.isAddingNewAssociate = false;
  }

  selectSubForm(formType: 'conversation' | 'examinee'): void {
    this.selectedSubForm = formType;
    this.mainForm.get('conversationDetails')?.disable();
    this.mainForm.get('examineeDetails')?.disable();

    if (formType === 'conversation') {
      this.mainForm.get('conversationDetails')?.enable();
    } else if (formType === 'examinee') {
      this.mainForm.get('examineeDetails')?.enable();
      this.getQuestionnaireList();
    }
  }

  onSubmit(): void {
    this.submitted = true;
    this.message = '';
    this.mainForm.markAllAsTouched();

    if (!this.isReInvite) {
      const assocControl = this.associationDetails.get('hasAssociation');
      const selectedAssocIdControl = this.associationDetails.get('selectedAssociateId');
      if (assocControl?.value === 'yes' && !selectedAssocIdControl?.value) {
        selectedAssocIdControl?.setErrors({ required: true });
        selectedAssocIdControl?.markAsTouched();
        return; 
      }
    }

    if (this.mainForm.invalid) { return; }
    
    this.spinner = true;
    const data = this.mainForm.getRawValue();

    if (this.isReInvite && this.reInviteExamineeId && this.assessment) {
       const payload = { expired_at: data.examineeDetails.time_expiration };
       
       this.assessmentService.reInviteExaminee(this.projectID, this.assessment.id, this.reInviteExamineeId, { activity_type: 'examinee' }, payload).subscribe({
         next: (res: any) => {
           this.assessmentChanged.emit({ ...res, action: 're-invite' });
           this.createAssessmentModalRef.close();
         },
         error: (err: any) => {
           this.submitted = false;
           this.spinner = false;
           this.message = err.error?.message || 'Error re-sending invitation!';
         }
       });
       return;
    }

    data.selectedSubForm = this.selectedSubForm;
    data.subjectSelected = this.subjectSelected;

    this.assessmentService.createAssessment(this.projectID, data).subscribe({
      next: (res) => {
        this.assessmentChanged.emit(res);
        this.createAssessmentModalRef.close();
      },
      error: () => {
        this.submitted = false;
        this.spinner = false;
        this.message = 'Error creating the assessment!';
      }
    });
  }

  // --- Cleanup ---
  private unsubscribeAll(): void {
    this.searchSubscription?.unsubscribe();
    this.usersSearchSubscription?.unsubscribe();
    this.questionnaireSearchSubscription?.unsubscribe();
    this.associationChoiceSubscription?.unsubscribe();
  }

  closeModal(): void {
    this.modalService.dismissAll();
    this.unsubscribeAll();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll();
  }
}
